import { useEffect, useState } from "react";
import { CheckCircle2, DollarSign, Percent } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

export default function AdminReferrals() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("pending");

  // Referral rate settings card state
  const [rateSetting, setRateSetting] = useState(null);        // {rate_pct, updated_at, updated_by_email}
  const [rateDraft, setRateDraft] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  // BLOCK 39 Item 8 ,  Referral-code expiration settings state
  const [expSetting, setExpSetting] = useState(null);          // {code_expiration_days, ...}
  const [expDraft, setExpDraft] = useState("");
  const [savingExp, setSavingExp] = useState(false);

  const load = () => {
    api.get("/referrals/admin/all", { params: { status_filter: filter || undefined } }).then((r) => setList(r.data));
    api.get("/referrals/admin/summary").then((r) => setSummary(r.data));
  };

  const loadRate = () => {
    api.get("/admin/settings/referral-rate").then((r) => {
      setRateSetting(r.data);
      setRateDraft(String(r.data.rate_pct));
    });
    api.get("/admin/settings/referral-code-expiration").then((r) => {
      setExpSetting(r.data);
      setExpDraft(String(r.data.code_expiration_days));
    });
  };

  useEffect(load, [filter]);
  useEffect(loadRate, []);

  const markPaid = async (rid) => {
    try {
      await api.post("/referrals/admin/payout", { rid });
      toast.success("Marked as paid");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveRate = async () => {
    const value = parseFloat(rateDraft);
    if (Number.isNaN(value) || value <= 0 || value > 50) {
      toast.error("Rate must be greater than 0 and at most 50");
      return;
    }
    setSavingRate(true);
    try {
      const r = await api.patch("/admin/settings/referral-rate", { rate_pct: value });
      setRateSetting(r.data);
      setRateDraft(String(r.data.rate_pct));
      toast.success(`Referral rate updated to ${r.data.rate_pct}%`);
      // Refresh summary so its rate tile matches immediately
      api.get("/referrals/admin/summary").then((res) => setSummary(res.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update rate");
    } finally {
      setSavingRate(false);
    }
  };

  const rateDirty = rateSetting && parseFloat(rateDraft) !== rateSetting.rate_pct;
  const rateInvalid = (() => {
    const v = parseFloat(rateDraft);
    return Number.isNaN(v) || v <= 0 || v > 50;
  })();

  const saveExp = async () => {
    const days = parseInt(expDraft, 10);
    if (Number.isNaN(days) || days < 1 || days > 3650) {
      toast.error("Expiration must be between 1 and 3650 days");
      return;
    }
    setSavingExp(true);
    try {
      const r = await api.patch("/admin/settings/referral-code-expiration", { code_expiration_days: days });
      setExpSetting(r.data);
      setExpDraft(String(r.data.code_expiration_days));
      toast.success(`Referral-code expiration updated to ${r.data.code_expiration_days} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update expiration");
    } finally {
      setSavingExp(false);
    }
  };

  const expDirty = expSetting && parseInt(expDraft, 10) !== expSetting.code_expiration_days;
  const expInvalid = (() => {
    const v = parseInt(expDraft, 10);
    return Number.isNaN(v) || v < 1 || v > 3650;
  })();

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin</div>
        <h1 className="font-heading text-4xl tracking-tight">Referral Payouts</h1>
        <p className="text-sm text-slate-400 mt-1">Process commissions to your affiliates</p>
      </header>

      {/* Referral Program Settings ,  Step 5 */}
      <div className="ae-card p-6 border-emerald-500/20" data-testid="referral-rate-settings-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4 text-emerald-400" />
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">Referral Program Settings</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Current rate</div>
            <div className="font-mono tabular-nums text-5xl font-bold text-emerald-400" data-testid="referral-rate-current">
              {rateSetting ? `${rateSetting.rate_pct}%` : ", "}
            </div>
            {rateSetting?.updated_at && (
              <div className="text-[11px] text-slate-500 mt-2" data-testid="referral-rate-updated-meta">
                Last updated: {fmtDateTime(rateSetting.updated_at)}
                {rateSetting.updated_by_email ? ` by ${rateSetting.updated_by_email}` : ""}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-[260px] max-w-md">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-2">
              New rate (0.01 - 50%)
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="number"
                  min="0.01"
                  max="50"
                  step="0.1"
                  value={rateDraft}
                  onChange={(e) => setRateDraft(e.target.value)}
                  className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 pr-8 text-lg font-mono tabular-nums focus:border-emerald-500/50 focus:outline-none"
                  data-testid="referral-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
              </div>
              <button
                onClick={saveRate}
                disabled={savingRate || !rateDirty || rateInvalid}
                data-testid="referral-rate-save-btn"
                className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingRate ? "Saving…" : "Save"}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
              Changes apply to new referrals going forward. Referrals already converted keep the rate active at the time they converted.
            </p>
          </div>
        </div>
      </div>

      {/* BLOCK 39 Item 8 ,  Referral-code expiration setting card */}
      <div className="ae-card p-6 border-cyan-500/20" data-testid="referral-code-expiration-card">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h2 className="font-heading text-lg">Referral Code Expiration</h2>
            <p className="text-xs text-slate-500 mt-1">
              Configure how long a newly-issued referral code stays valid before signups no longer attribute back.
              Existing codes keep whatever window was locked in at their original issuance.
            </p>
          </div>
          <div className="text-right">
            <div className="font-mono tabular-nums text-5xl font-bold text-cyan-400" data-testid="referral-code-expiration-current">
              {expSetting ? `${expSetting.code_expiration_days}d` : "…"}
            </div>
            {expSetting?.code_expiration_updated_at && (
              <div className="text-[11px] text-slate-500 mt-2" data-testid="referral-code-expiration-updated-meta">
                Last change: {fmtDateTime(expSetting.code_expiration_updated_at)}
                {expSetting.code_expiration_updated_by_email ? ` by ${expSetting.code_expiration_updated_by_email}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-end gap-3">
          <label className="block flex-1 max-w-[200px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">New expiration (days)</div>
            <input
              type="number"
              min="1"
              max="3650"
              step="1"
              value={expDraft}
              onChange={(e) => setExpDraft(e.target.value)}
              data-testid="referral-code-expiration-input"
              className={`w-full bg-white/3 border rounded-sm px-3 py-2 text-slate-200 font-mono tabular-nums ${expInvalid ? "border-rose-500/50" : "border-white/15"}`}
            />
          </label>
          <button
            onClick={saveExp}
            disabled={savingExp || !expDirty || expInvalid}
            data-testid="referral-code-expiration-save-btn"
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {savingExp ? "Saving…" : "Save"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
          Range: 1-3,650 days (10 years max). The expiration window active at code issuance is locked onto the user's record; later changes only affect codes issued after the change.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="ae-card p-5 border-amber-500/30">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Pending payouts</div>
            <div className="font-mono tabular-nums text-3xl font-bold mt-2 text-amber-400" data-testid="admin-ref-pending">${summary.pending.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{summary.pending.count} commissions</div>
          </div>
          <div className="ae-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Lifetime paid</div>
            <div className="font-mono tabular-nums text-3xl font-bold mt-2 text-emerald-400">${summary.paid.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{summary.paid.count} commissions</div>
          </div>
          <div className="ae-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Commission rate</div>
            <div className="font-mono tabular-nums text-3xl font-bold mt-2 text-slate-100">{(summary.commission_rate * 100).toFixed(0)}%</div>
            <div className="text-xs text-slate-500 mt-1">Recurring on renewals</div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {["pending", "paid", ""].map((f) => (
          <button
            key={f || "all"}
            data-testid={`ref-filter-${f || "all"}`}
            onClick={() => setFilter(f)}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-all ${filter === f ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50" : "border-white/10 text-slate-400 hover:bg-white/5"}`}
          >
            {f || "all"}
          </button>
        ))}
      </div>

      <div className="ae-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Referrer</th>
              <th className="text-left px-4 py-3">Referred</th>
              <th className="text-right px-4 py-3">Payment</th>
              <th className="text-right px-4 py-3">Commission</th>
              <th className="text-right px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-500 py-12">
                <DollarSign className="h-8 w-8 mx-auto text-slate-700 mb-2" /> No commissions in this view
              </td></tr>
            ) : list.map((c) => (
              <tr key={c.rid} data-testid={`ref-row-${c.rid}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-xs font-mono text-slate-400">{fmtDateTime(c.created_at)}</td>
                <td className="px-4 py-3">
                  <div>{c.referrer?.name || "N/A"}</div>
                  <div className="text-[11px] text-slate-500">{c.referrer?.email}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-300">{c.referred_email}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">${c.payment_amount.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-400">${c.commission_amount.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {c.status === "pending" && (
                    <button onClick={() => markPaid(c.rid)} data-testid={`mark-paid-${c.rid}`} className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Mark paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
